import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { Mic, Send } from 'react-feather';
import InputField from '@/UIlibrary/InputField/InputField';
import VoiceRecordModal from './VoiceRecordModal';
import { useSupabaseStore } from './supabaseStore';
import IconButton from '@/UIlibrary/IconButton/IconButton';
import MessageAttachmentDropdown from './MessageAttachmentDropdown';

interface MessageInputProps {
	onSendMessage: (
		message: string,
		options?: {
			type?: 'text' | 'audio' | 'voice';
			audioUrl?: string;
			chatId?: string;
			messageId?: string;
		}
	) => Promise<boolean>;
	disabled?: boolean;
	chatId: string; // Add chatId prop
}

const MAX_TEXT_LENGTH_ALLOWED_BY_WA = 4086;
const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, disabled = false, chatId }) => {
	const t = useTranslations('Support');
	const [newMessage, setNewMessage] = useState('');
	const [isSending, setIsSending] = useState(false);
	const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
	const [inputHasLengthError, setInputHasLengthError] = useState(false);

	const supabase = useSupabaseStore((state) => state.supabase);

	const handleSubmit = async () => {
		if (!newMessage.trim() || isSending) return;
		setIsSending(true);
		const success = await onSendMessage(newMessage);
		if (success) {
			setNewMessage('');
		}
		setIsSending(false);
	};

	const handleVoiceNote = async (audioBlob: Blob) => {
		try {
			setIsSending(true);

			const messageId = `${Date.now()}-${Math.random().toString(36)?.substr(2, 9)}`;

			const filename = `${chatId}/${messageId}.mp3`;
			const bucket = 'whatsapp-voice-notes';

			// Upload to Supabase Storage with organized structure
			const { error: uploadError } = await supabase.storage
				.from(bucket)
				.upload(filename, audioBlob, {
					contentType: 'audio/mpeg',
					cacheControl: '3600',
					upsert: true
				});

			if (uploadError) throw uploadError;

			const {
				data: { publicUrl }
			} = supabase.storage.from(bucket).getPublicUrl(filename);

			await onSendMessage('', {
				type: 'voice',
				audioUrl: publicUrl,
				chatId,
				messageId
			});
		} catch (error) {
			console.error('Error handling voice note:', error);
		} finally {
			setIsSending(false);
			setIsRecordModalOpen(false);
		}
	};

	return (
		<>
			<div className="p-4 border-t">
				<div className="flex gap-2 items-center justify-center w-full max-h-[calc(20vh)] lg:max-h-[calc(10vh)] mb-12">
					<MessageAttachmentDropdown chatId={chatId} />
					<div className="w-full">
						<InputField
							type="text"
							size="auto"
							value={newMessage}
							onChange={(e) => setNewMessage(e.target.value)}
							placeholder={t('TypeMessage')}
							disabled={disabled || isSending}
							maxChars={MAX_TEXT_LENGTH_ALLOWED_BY_WA}
							error={inputHasLengthError}
							charLenghtError={t('MaxCharsError')}
							hasCharLengthError={(error: boolean) => {
								setInputHasLengthError(error);
							}}
							endAction={<button onClick={handleSubmit} />}
						/>
					</div>
					<div>
						{!newMessage.trim() ? (
							<IconButton
								color="primary"
								type="button"
								size="large"
								onClick={() => setIsRecordModalOpen(true)}
								disabled={disabled || isSending}
								icon={<Mic />}
							/>
						) : (
							<IconButton
								color="primary"
								type="submit"
								size="large"
								icon={<Send />}
								disabled={disabled || isSending || !newMessage.trim()}
								onClick={handleSubmit}
							/>
						)}
					</div>
				</div>
			</div>
			<VoiceRecordModal
				isOpen={isRecordModalOpen}
				onClose={() => setIsRecordModalOpen(false)}
				onSave={handleVoiceNote}
			/>
		</>
	);
};

export default MessageInput;
